import z from 'zod';

import type {ProductColorFragment} from '@/modules/commerce/product-color/query';

import {productColorConfigurationFromFormData} from '../../../product-color/configuration';

import type {CabFloorMatMaterialCoreFragment} from './query';

export const cabFloorMatMaterialConfigurationSchema = z.object({
	materialColorId: z.string().optional(),
	stitchingColorId: z.string().optional(),
});

export type CabFloorMatMaterialConfiguration = z.infer<
	typeof cabFloorMatMaterialConfigurationSchema
>;

export type CabFloorMatMaterialConfigurationValidationResult = {
	message?: string;

	materialColorError?: {
		message: string;
	};
	stitchingColorError?: {
		message: string;
	};
};

export function getInitialCabFloorMatMaterialConfiguration(
	materials: CabFloorMatMaterialCoreFragment[],
): CabFloorMatMaterialConfiguration | undefined {
	if (!materials) {
		return;
	}

	const firstMaterial = materials.at(0);
	const defaultMaterialColorId = firstMaterial?.materialColor?._id;
	if (!defaultMaterialColorId) {
		return;
	}

	const defaultStitchingColorId = firstMaterial?.stitchingColors?.at(0)?._id;

	return {
		materialColorId: defaultMaterialColorId,
		stitchingColorId: defaultStitchingColorId,
	};
}

export function getUniqueMaterialColors(materials: CabFloorMatMaterialCoreFragment[]) {
	const colors = new Map<string, ProductColorFragment>();

	for (const material of materials) {
		if (material.materialColor) {
			colors.set(material.materialColor._id, material.materialColor);
		}
	}

	return Array.from(colors.values());
}

export function getUniqueStitchingColors(material: CabFloorMatMaterialCoreFragment) {
	const colors = new Map<string, ProductColorFragment>();

	for (const stitchingColor of material.stitchingColors ?? []) {
		colors.set(stitchingColor._id, stitchingColor);
	}

	return Array.from(colors.values());
}

export function cabFloorMatMaterialConfigurationFromFormData(
	colors: CabFloorMatMaterialCoreFragment[],
	name: string,
	formData: FormData,
) {
	let config = getInitialCabFloorMatMaterialConfiguration(colors);

	const materialColors = getUniqueMaterialColors(colors);

	const materialColorId = productColorConfigurationFromFormData(
		materialColors,
		`${name}.materialColorId`,
		formData,
	);

	if (materialColorId) {
		const selectedColor = colors.find(
			(option) => option.materialColor?._id && option.materialColor._id === materialColorId,
		);

		const stitchingColors = selectedColor ? getUniqueStitchingColors(selectedColor) : [];

		const stitchingColorId = productColorConfigurationFromFormData(
			stitchingColors,
			`${name}.stitchingColorId`,
			formData,
		);

		config = {
			materialColorId,
			stitchingColorId: stitchingColorId ?? stitchingColors.at(0)?._id,
		};
	}

	return config;
}
